import { useMemo, useState } from "react";
import { BookOpen, CheckCircle2, CircleDollarSign, Clock, Plus, Trash2 } from "lucide-react";
import { api, money, shortDate } from "@/util";

const TABS = [
  { key: "all", label: "Tüm hareketler" },
  { key: "income", label: "Gelir" },
  { key: "expense", label: "Gider" },
  { key: "pending", label: "Bekleyen" },
];

export default function Finance({ finance, user, onNew, onChanged }) {
  const [tab, setTab] = useState("all");
  const isManager = user?.role === "Yönetici";

  const rows = useMemo(() => {
    switch (tab) {
      case "income": return finance.filter((x) => x.type === "income");
      case "expense": return finance.filter((x) => x.type === "expense");
      case "pending": return finance.filter((x) => x.status === "Bekliyor");
      default: return finance;
    }
  }, [finance, tab]);

  const totals = useMemo(() => {
    const inc = finance.filter((x) => x.type === "income").reduce((a, x) => a + x.amount, 0);
    const exp = finance.filter((x) => x.type === "expense").reduce((a, x) => a + x.amount, 0);
    return { inc, exp, net: inc - exp };
  }, [finance]);

  const complete = async (id) => {
    await api.patch(`/finance/${id}`, { status: "Tamamlandı" });
    onChanged?.();
  };

  const revert = async (id) => {
    await api.patch(`/finance/${id}`, { status: "Bekliyor" });
    onChanged?.();
  };

  const remove = async (id) => {
    if (!isManager) return;
    if (!window.confirm("Bu kayıt silinsin mi? Hesap bakiyesi de güncellenecek.")) return;
    await api.delete(`/finance/${id}`);
    onChanged?.();
  };

  return (
    <section className="page-section">
      <div className="section-header">
        <div>
          <span className="eyebrow">FİNANS MERKEZİ</span>
          <h2>Gelir, gider ve bekleyenler</h2>
          <p>Tüm hareketleri filtrele, bekleyen ödemeleri tek tıkla tamamla.</p>
        </div>
        <div className="button-group">
          <button className="outline-button" onClick={() => onNew("income")} data-testid="finance-add-income">
            <Plus size={16} /> Gelir
          </button>
          <button className="primary-button" onClick={() => onNew("expense")} data-testid="finance-add-expense">
            <Plus size={16} /> Gider
          </button>
        </div>
      </div>

      <div className="finance-summary">
        <div className="finance-chip positive" data-testid="finance-total-income">
          <small>Toplam Gelir</small><b>{money(totals.inc)}</b>
        </div>
        <div className="finance-chip negative" data-testid="finance-total-expense">
          <small>Toplam Gider</small><b>{money(totals.exp)}</b>
        </div>
        <div className={`finance-chip ${totals.net >= 0 ? "positive" : "negative"}`} data-testid="finance-total-net">
          <small>Net</small><b>{money(totals.net)}</b>
        </div>
      </div>

      <div className="tabs-row">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`tab ${tab === t.key ? "active" : ""}`}
            data-testid={`finance-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {rows.length ? (
        <div className="table-wrap">
          <table className="data-table" data-testid="finance-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Başlık</th>
                <th>Kategori</th>
                <th>Hesap</th>
                <th>Durum</th>
                <th className="right">Tutar</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} data-testid={`finance-row-${idx}`}>
                  <td>{shortDate(r.date)}</td>
                  <td><b>{r.title}</b></td>
                  <td className="muted">{r.category}</td>
                  <td className="muted">{r.account}</td>
                  <td>
                    <span className={`pill ${r.status === "Tamamlandı" ? "success" : "warning"}`}>
                      {r.status === "Tamamlandı" ? <CheckCircle2 size={11} /> : <Clock size={11} />} {r.status}
                    </span>
                  </td>
                  <td className={`right ${r.type === "income" ? "positive" : "negative"}`}>
                    <b>{r.type === "income" ? "+" : "-"}{money(r.amount)}</b>
                  </td>
                  <td className="actions">
                    {r.status === "Bekliyor" ? (
                      <button className="icon-button small" onClick={() => complete(r.id)} data-testid={`finance-complete-${idx}`} title="Tamamla">
                        <CheckCircle2 size={14} />
                      </button>
                    ) : (
                      <button className="icon-button small" onClick={() => revert(r.id)} data-testid={`finance-revert-${idx}`} title="Beklemede olarak işaretle">
                        <Clock size={14} />
                      </button>
                    )}
                    {isManager && (
                      <button className="icon-button small danger" onClick={() => remove(r.id)} data-testid={`finance-delete-${idx}`} title="Sil">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-page">
          <div className="empty-icon">
            {tab === "income" ? <CircleDollarSign size={22} /> : tab === "expense" ? <BookOpen size={22} /> : <Clock size={22} />}
          </div>
          <h2>Bu sekmede kayıt yok</h2>
          <p>Üstteki Gelir / Gider butonlarıyla ilk hareketini oluşturabilirsin.</p>
        </div>
      )}
    </section>
  );
}
